import { BrandKit } from './brand.js';
export const MARKEN_STILE = [
    { id: 'klar', name: 'Klar', satz: 'Farbstreifen und dunkler Kasten' },
    { id: 'kraeftig', name: 'Kräftig', satz: 'Volle Farbe, grosse Schrift' },
    { id: 'ruhig', name: 'Ruhig', satz: 'Ohne Kasten, feine Linie' },
];
/** Fertige Farbpaare für alle ohne Logo -- vorne die Farbe, hinten der Grund. */
export const FARB_VORSCHLAEGE = [
    { name: 'Rot', primary: '#e11d48', secondary: '#0f172a' },
    { name: 'Blau', primary: '#2563eb', secondary: '#0b1b3a' },
    { name: 'Grün', primary: '#16a34a', secondary: '#0f2a1b' },
    { name: 'Orange', primary: '#f97316', secondary: '#1f2328' },
    { name: 'Violett', primary: '#8b5cf6', secondary: '#1b1033' },
    { name: 'Gelb', primary: '#facc15', secondary: '#111111' },
];
const hex = (r, g, b) => `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`;
function rgb(farbe) {
    const n = parseInt(farbe.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
/** Helligkeit wie das Auge sie sieht, 0 bis 1 (Rec. 709, ohne Gamma -- reicht zum Sortieren). */
export function helligkeit(farbe) {
    const [r, g, b] = rgb(farbe);
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}
function saettigung(r, g, b) {
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    return max === 0 ? 0 : (max - min) / max;
}
/** Eine Farbe zum Grund abdunkeln: so weit, dass weisse Schrift darauf steht. */
export function alsGrund(farbe) {
    let [r, g, b] = rgb(farbe);
    for (let i = 0; i < 12 && helligkeit(hex(r, g, b)) > 0.18; i++) {
        r *= 0.75;
        g *= 0.75;
        b *= 0.75;
    }
    return hex(r, g, b);
}
/**
 * **Die Farben eines Logos**, aus seinen Bildpunkten (RGBA hintereinander,
 * wie `ImageData.data`).
 *
 * Durchsichtiges zählt nicht, fast Weisses und fast Schwarzes auch nicht --
 * das ist der Grund, auf dem ein Logo steht, nicht seine Farbe. Gezählt wird
 * in groben Fächern (je Kanal 16 Stufen), gewichtet mit der Sättigung: ein
 * kleines rotes Zeichen auf viel Grau ist ein rotes Logo. Die zweite Farbe
 * ist die häufigste, die sich deutlich von der ersten unterscheidet; sie wird
 * zum Grund abgedunkelt, damit weisse Schrift darauf lesbar bleibt. Gibt es
 * keine zweite, wird die erste selbst zum Grund.
 *
 * `null`, wenn das Bild keine Farbe hat -- ein schwarzweisses Logo. Dann
 * gelten die fertigen Farbpaare.
 */
export function farbenAusBild(punkte) {
    const faecher = new Map();
    for (let i = 0; i + 3 < punkte.length; i += 4) {
        const r = punkte[i];
        const g = punkte[i + 1];
        const b = punkte[i + 2];
        if (punkte[i + 3] < 128)
            continue;
        const l = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
        if (l > 0.93 || l < 0.06)
            continue;
        const s = saettigung(r, g, b);
        const schluessel = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
        const f = faecher.get(schluessel) ?? { n: 0, r: 0, g: 0, b: 0, gewicht: 0 };
        f.n += 1;
        f.r += r;
        f.g += g;
        f.b += b;
        f.gewicht += 0.25 + s;
        faecher.set(schluessel, f);
    }
    const liste = [...faecher.values()]
        .map((f) => ({ farbe: hex(f.r / f.n, f.g / f.n, f.b / f.n), gewicht: f.gewicht, n: f.n }))
        .sort((a, b) => b.gewicht - a.gewicht);
    const erste = liste[0];
    if (!erste)
        return null;
    const [r1, g1, b1] = rgb(erste.farbe);
    if (saettigung(r1, g1, b1) < 0.15)
        return null;
    const abstand = (farbe) => {
        const [r, g, b] = rgb(farbe);
        return Math.hypot(r - r1, g - g1, b - b1);
    };
    const zweite = liste.slice(1).find((f) => abstand(f.farbe) > 90);
    return { primary: erste.farbe, secondary: alsGrund(zweite?.farbe ?? erste.farbe) };
}
/** Die drei Vorlagen eines Stils. */
export function stilVorlagen(stil) {
    const bug = {
        id: 'bug',
        name: 'Sender-Logo',
        kind: 'bug',
        defaultDuration: 10,
        animation: 'fade',
        layers: [{ type: 'logo', x: 0.86, y: 0.06, w: 0.1, opacity: 0.9 }],
    };
    const text = (slot, placeholder, x, y, size, weiter) => ({
        type: 'text',
        slot,
        placeholder,
        x,
        y,
        maxWidth: weiter.maxWidth ?? 0.8,
        color: weiter.color ?? null,
        style: {
            fontFamily: 'Inter',
            size,
            color: weiter.farbe ?? '#ffffff',
            boxColor: null,
            boxOpacity: 0.6,
            outlineColor: null,
            outlineWidth: 0.08,
            align: weiter.align ?? 'left',
            shadow: weiter.shadow ?? false,
            bold: weiter.bold ?? false,
            uppercase: weiter.uppercase ?? false,
        },
    });
    if (stil === 'kraeftig') {
        return [
            {
                id: 'lower-third',
                name: 'Bauchbinde',
                kind: 'lowerThird',
                defaultDuration: 4,
                animation: 'slideLeft',
                layers: [
                    { type: 'rect', color: 'primary', opacity: 1, x: 0.06, y: 0.645, w: 0.66, h: 0.075 },
                    { type: 'rect', color: 'secondary', opacity: 1, x: 0.06, y: 0.72, w: 0.5, h: 0.05 },
                    text('name', 'Vorname Nachname', 0.085, 0.657, 0.036, { bold: true, uppercase: true, maxWidth: 0.6 }),
                    text('role', 'Funktion', 0.085, 0.73, 0.024, { maxWidth: 0.45 }),
                ],
            },
            {
                id: 'title-card',
                name: 'Titel',
                kind: 'title',
                defaultDuration: 3,
                animation: 'fade',
                layers: [
                    { type: 'rect', color: 'primary', opacity: 1, x: 0, y: 0, w: 1, h: 1 },
                    text('title', 'Titel', 0.5, 0.42, 0.095, { align: 'center', bold: true, uppercase: true }),
                    { type: 'rect', color: 'secondary', opacity: 1, x: 0.3, y: 0.545, w: 0.4, h: 0.05 },
                    text('subtitle', 'Untertitel', 0.5, 0.555, 0.03, { align: 'center' }),
                ],
            },
            bug,
        ];
    }
    if (stil === 'ruhig') {
        return [
            {
                id: 'lower-third',
                name: 'Bauchbinde',
                kind: 'lowerThird',
                defaultDuration: 4,
                animation: 'fade',
                layers: [
                    text('name', 'Vorname Nachname', 0.08, 0.66, 0.034, { shadow: true, maxWidth: 0.7 }),
                    { type: 'rect', color: 'primary', opacity: 1, x: 0.08, y: 0.708, w: 0.22, h: 0.004 },
                    text('role', 'Funktion', 0.08, 0.718, 0.024, { farbe: '#e5e7eb', shadow: true, maxWidth: 0.7 }),
                ],
            },
            {
                id: 'title-card',
                name: 'Titel',
                kind: 'title',
                defaultDuration: 3,
                animation: 'fade',
                layers: [
                    { type: 'rect', color: 'secondary', opacity: 1, x: 0, y: 0, w: 1, h: 1 },
                    text('title', 'Titel', 0.5, 0.43, 0.075, { align: 'center' }),
                    { type: 'rect', color: 'primary', opacity: 1, x: 0.44, y: 0.53, w: 0.12, h: 0.004 },
                    text('subtitle', 'Untertitel', 0.5, 0.55, 0.03, { align: 'center', farbe: '#d1d5db' }),
                ],
            },
            bug,
        ];
    }
    return [
        {
            id: 'lower-third',
            name: 'Bauchbinde',
            kind: 'lowerThird',
            defaultDuration: 4,
            animation: 'slideLeft',
            layers: [
                { type: 'rect', color: 'primary', opacity: 0.95, x: 0.06, y: 0.655, w: 0.012, h: 0.115 },
                { type: 'rect', color: 'secondary', opacity: 0.75, x: 0.072, y: 0.655, w: 0.62, h: 0.115 },
                text('name', 'Vorname Nachname', 0.095, 0.672, 0.032, { bold: true, maxWidth: 0.55 }),
                text('role', 'Funktion', 0.095, 0.716, 0.024, { farbe: '#e2e8f0', maxWidth: 0.55 }),
            ],
        },
        {
            id: 'title-card',
            name: 'Titel',
            kind: 'title',
            defaultDuration: 3,
            animation: 'fade',
            layers: [
                { type: 'rect', color: 'secondary', opacity: 1, x: 0, y: 0, w: 1, h: 1 },
                text('title', 'Titel', 0.5, 0.44, 0.085, { align: 'center', bold: true }),
                text('subtitle', 'Untertitel', 0.5, 0.56, 0.035, { align: 'center', color: 'primary' }),
            ],
        },
        bug,
    ];
}
/**
 * Das fertige Markenset. Was schon da ist (Logo, Schriftdatei), bleibt -- der
 * Assistent ersetzt Farben und Vorlagen, nicht die hochgeladenen Dateien.
 */
export function baueMarkenset(basis, wahl) {
    return BrandKit.parse({
        ...basis,
        primary: wahl.primary,
        secondary: wahl.secondary,
        templates: stilVorlagen(wahl.stil),
    });
}
//# sourceMappingURL=markenAssistent.js.map